import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { filter, map, switchMap, take, BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsArticle } from '@fcom/core-api/interfaces';
import { finShare } from '@fcom/rx';
import { ButtonMode, ButtonTheme, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { compareAsJson, groupBy, toLookup, unique, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, FragmentService, GaContext, GtmEvent, GtmPurchaseFlow } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { getGA4ProductsFromSmpProducts } from '@fcom/common/utils/gtm.utils';
import {
  FinnairPassengerServiceSelectionItemWithStatus,
  FinnairServiceItemWithUpsell,
  FlightServices,
  ModifiedServiceInfo,
  ModifiedServiceProgress,
} from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
} from '@fcom/dapi/api/models';

import { SmpProduct } from '../product-list/product-list.component';
import { getGroupedServices, isFlight } from '../../../../utils/common-booking.utils';
import { getPassengerTierLevel, isBoundBasedCategory } from '../../utils';
import { AllowedActionsMode } from '../../enums';

@Component({
  selector: 'fin-dapi-included-service',
  styleUrls: ['./included-service.component.scss'],
  templateUrl: './included-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IncludedServiceComponent implements OnDestroy, OnInit, OnChanges {
  private gtmService = inject(GtmService);
  private fragmentService = inject(FragmentService);

  readonly Category = Category;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;

  private subscriptions: Subscription = new Subscription();

  @Input() service: FinnairServiceItemWithUpsell;
  @Input() showAsPending = false;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() passengers$: Observable<FinnairPassengerItem[]> = of([]);
  @Input() showUpsellPerPax = false;
  @Input() showUpsellPaymentButton = true;
  @Input() includeSeatActionButtons = false;
  @Input() products$: Observable<SmpProduct[]> = new BehaviorSubject([]);
  @Input() gtmFlow: GtmPurchaseFlow = GtmPurchaseFlow.MANAGE_BOOKING;
  @Input() gaContext: GaContext = GaContext.MANAGE_BOOKING;
  @Input() isCarrierConnect = false;
  @Input() includeAssignAtAirportStatus = false;
  @Input() showNonAYSeats = false;
  @Input() mode: AllowedActionsMode = AllowedActionsMode.INFO;
  @Input() groupServicesByBound = false;
  serviceModifyProgress = input.required<ModifiedServiceProgress>();

  @Output() selectProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();
  @Output() continuePurchase: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() removeProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();
  @Output() viewSeatmap: EventEmitter<void> = new EventEmitter();

  serviceSegmentItems$: Observable<FlightServices[]>;
  serviceSubject$: BehaviorSubject<FinnairServiceItem> = new BehaviorSubject<FinnairServiceItem>(null);
  hasNonAySegments$: Observable<boolean>;
  safProductInfo$: Observable<CmsArticle>;
  safThankYouForJoiningUsNotification$: Observable<CmsArticle>;
  isModalOpened = false;
  serviceModifyProgress$: Observable<ModifiedServiceProgress>;

  constructor() {
    this.serviceModifyProgress$ = toObservable(this.serviceModifyProgress);
  }

  ngOnInit(): void {
    this.serviceSubject$.next(this.service);
    this.serviceSegmentItems$ = this.findServiceInAllFlights();
    this.hasNonAySegments$ = this.serviceSegmentItems$.pipe(
      map((segments) => segments.some((seg) => seg.operatingAirline.code !== 'AY'))
    );

    this.safProductInfo$ = this.serviceSubject$.pipe(
      filter((service) => service.category === Category.SAF),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.services.productInfo.saf.contributeUrl'))
    );
    this.safThankYouForJoiningUsNotification$ = this.serviceSubject$.pipe(
      filter((service) => service.category === Category.SAF),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.thankYouForJoiningUs.url'))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.service && !changes.service.isFirstChange()) {
      this.serviceSubject$.next(this.service);
    }
  }

  private findServiceInAllFlights(): Observable<FlightServices[]> {
    const isPassengerSelected = (
      selectedPassengers: FinnairPassengerItem[],
      passengerServiceItem: FinnairPassengerServiceItem
    ): boolean =>
      selectedPassengers.length === 0 || selectedPassengers.some(({ id }) => id === passengerServiceItem.id);

    return combineLatest([
      this.cartOrOrder$,
      combineLatest([this.passengers$, this.serviceSubject$]).pipe(
        map(([passengers, service]) =>
          service.category === Category.SEAT
            ? passengers.filter((pax) => pax.passengerTypeCode !== FinnairPassengerCode.INF)
            : passengers
        )
      ),
      this.serviceSubject$,
      this.serviceModifyProgress$,
    ]).pipe(
      map(([order, selectedPassengers, service, modifyInProgressInfo]) => {
        const { bounds, serviceCatalog } = order;
        const variantsWeCanSell =
          serviceCatalog?.categories
            ?.flatMap((serviceCategory) =>
              Object.values(serviceCategory.services).flatMap((servicesForFragment) =>
                Object.values(servicesForFragment).flatMap((passengerServices) =>
                  passengerServices.flatMap((passengerService) => passengerService.variant)
                )
              )
            )
            .filter(unique) ?? [];
        const allFlights = bounds.flatMap((bound) => bound.itinerary).filter(isFlight);
        const flightById = toLookup(allFlights, (flight) => flight.id);

        const serviceCatalogPerCategory = serviceCatalog?.categories.find(
          ({ category }) =>
            category === this.service.category &&
            [
              Category.CABIN_BAGGAGE,
              Category.BAGGAGE,
              Category.SEAT,
              Category.MEAL,
              Category.WIFI,
              Category.SPORT,
              Category.LOUNGE,
              Category.PET,
              Category.SAF,
              Category.COVER,
            ].includes(category)
        );

        const allServiceSegments: FinnairServiceSegmentItem[] = service.bounds.flatMap(({ segments }) => segments);
        const serviceSegmentsByFlightId = groupBy(allServiceSegments, (segment) => segment.id);

        const serviceBySegments = Object.entries(serviceSegmentsByFlightId)
          .map(([flightId, serviceSegmentsForFlight]) => {
            const passengerServices = serviceSegmentsForFlight
              .flatMap(({ passengers }) => passengers)
              .filter((passenger) => isPassengerSelected(selectedPassengers, passenger));

            const passengerIncludedServices = passengerServices.map((passenger) =>
              passenger.services.filter((s) => s.includedInTicketType)
            );

            const complimentaryServices =
              service.category === Category.MEAL
                ? passengerIncludedServices.reduce((shared, services) => {
                    return shared.filter((value) => services.some((service) => service.variant === value.variant));
                  })
                : [];

            const variantsToRemove = complimentaryServices.map((service) => service.variant);

            const flightPassengers = passengerServices.map((passenger) => {
              const boundId = Object.keys(serviceCatalogPerCategory?.lowestPrice || {}).find((boundId) =>
                boundId.includes(flightId)
              );

              const isAvailableForSellServices =
                boundId && serviceCatalogPerCategory?.lowestPrice[boundId][passenger.id] && this.showUpsellPerPax;

              const showedServices =
                service.category === Category.MEAL
                  ? passenger.services.filter((service) => !variantsToRemove.includes(service.variant))
                  : passenger.services;

              return {
                ...passenger,
                passengerTypeCode: selectedPassengers.find((p) => p.id === passenger.id)?.passengerTypeCode,
                groupedServices: getGroupedServices(
                  showedServices,
                  getPassengerTierLevel(passenger.id, selectedPassengers),
                  variantsWeCanSell,
                  isAvailableForSellServices
                    ? [
                        {
                          id: '',
                          subCategory: `${service.category}`,
                          totalPrice: serviceCatalogPerCategory?.lowestPrice[boundId][passenger.id].money,
                          variant: serviceCatalogPerCategory?.services?.[boundId]?.[passenger.id]?.[0]?.variant,
                        } as unknown as FinnairPassengerServiceSelectionItem,
                      ]
                    : [],
                  this.isCarrierConnect,
                  this.includeAssignAtAirportStatus
                ),
              };
            });

            const serviceSegmentItem = serviceSegmentsForFlight[serviceSegmentsForFlight.length - 1];
            const flight = flightById[flightId];

            return {
              ...serviceSegmentItem,
              operatingAirline: flight.operatingAirline,
              passengers:
                service.category !== Category.SEAT || flight.operatingAirline.code === 'AY'
                  ? flightPassengers.filter((passenger) => passenger.groupedServices.length > 0)
                  : flightPassengers,
              complimentaryServices,
            } as FlightServices;
          })
          .filter((s) => s.passengers?.length > 0 || s.complimentaryServices.length > 0);
        const flightServices =
          this.groupServicesByBound && isBoundBasedCategory(this.service.category)
            ? this.mergeServicesForBound(serviceBySegments, bounds)
            : serviceBySegments;
        return this.updateModifyStatus(flightServices, modifyInProgressInfo);
      }),
      finShare()
    );
  }

  private updateModifyStatus(flightServices: FlightServices[], modifyServiceInfo: ModifiedServiceProgress) {
    return flightServices.map((flightService) => {
      return {
        ...flightService,
        passengers: flightService.passengers.map((pax) => {
          return {
            ...pax,
            groupedServices: pax.groupedServices.map((groupedService) => {
              return {
                ...groupedService,
                services: groupedService.services.map((service) => {
                  return {
                    ...service,
                    modifyInProgress: this.isModifyInProgressForService(
                      modifyServiceInfo,
                      flightService,
                      pax.id,
                      service
                    ),
                  };
                }),
              };
            }),
          };
        }),
      };
    });
  }

  private isModifyInProgressForService(
    modifyServiceInfo: ModifiedServiceProgress,
    flightServices: FlightServices,
    paxId: string,
    service: FinnairPassengerServiceSelectionItemWithStatus
  ) {
    if (!modifyServiceInfo.modifyInProgress) {
      return false;
    }

    const matchingService = modifyServiceInfo.services.find((modifyingService) => {
      return modifyingService.category === this.service.category && modifyingService.travelerId === paxId;
    });

    if (!matchingService) {
      return false;
    }

    if (
      this.service.category === Category.SEAT
        ? service.seatNumber !== matchingService.seatNumber
        : service.variant !== matchingService.variant
    ) {
      return false;
    }

    if (!this.groupServicesByBound && isBoundBasedCategory(this.service.category)) {
      return matchingService.boundId.includes(flightServices.id);
    } else {
      return flightServices.id === matchingService.fragmentId || flightServices.id === matchingService.boundId;
    }
  }

  private mergeServicesForBound(flightServices: FlightServices[], bounds: FinnairBoundItem[]): FlightServices[] {
    return bounds
      .map((bound) => {
        const flightIds = bound.itinerary.filter(isFlight).map((f) => f.id);
        const allFlightServicesInBound = flightServices.filter((flightService) => flightIds.includes(flightService.id));

        if (allFlightServicesInBound.length === 0) {
          return null;
        }
        const servicesGroupedByBound = [
          {
            ...allFlightServicesInBound[0],
            departure: bound.departure,
            arrival: bound.arrival,
            id: bound.id,
          },
        ];

        return this.isSegmentServicesIdentical(allFlightServicesInBound)
          ? servicesGroupedByBound
          : allFlightServicesInBound;
      })
      .filter((bound) => !!bound)
      .reduce((allServices, currentServices) => {
        return [...allServices, ...currentServices];
      }, []);
  }

  private isSegmentServicesIdentical(flightServices: FlightServices[]): boolean {
    const normalizePassengers = (passengers: FlightServices['passengers']) =>
      JSON.parse(JSON.stringify(passengers).replace(/ST\d+/g, 'ST'));

    const referencePassengers = normalizePassengers(flightServices[0].passengers);
    return flightServices.every((flightService) =>
      compareAsJson(normalizePassengers(flightService.passengers), referencePassengers)
    );
  }

  onContinuePurchase(): void {
    this.gtmService.trackElement(
      'continue-to-review_view-cta',
      this.gaContext,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.continuePurchase.emit(true);
  }

  onRemoveProduct(productInfo: ModifiedServiceInfo, travelerId: string, fragmentId: string): void {
    this.removeProduct.emit({
      ...productInfo,
      travelerId,
      fragmentId,
    });
  }

  onSelectProduct(productInfo: ModifiedServiceInfo, travelerId: string, fragmentId: string): void {
    this.selectProduct.emit({
      ...productInfo,
      travelerId,
      fragmentId,
    });

    this.subscriptions.add(
      combineLatest([this.products$, this.cartOrOrder$])
        .pipe(
          take(1),
          filter(([products]) => products.length > 0)
        )
        .subscribe(([products, cartOrOrder]) => {
          const matchedProduct = products.find((product) => product.category === productInfo.category);

          this.gtmService.ecommerceEventGA4(
            GtmEvent.SELECT_ITEM,
            getGA4ProductsFromSmpProducts([matchedProduct], this.gtmFlow, cartOrOrder.bounds)
          );
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
